// Wording and visibility rules for the "Email the client about this upload" option
// on the Client Folder upload and add-link dialogs (MWA-408).

export type UploadNoticeStatus = 'sent' | 'no_email' | 'failed'

export type UploadNoticeSeverity = 'success' | 'warning' | 'error'

// The option only applies while the files are visible to the client; a hidden file
// is never announced.
export function canEmailClientAboutUpload(publishImmediately: boolean): boolean {
  return publishImmediately
}

// The ids to announce: files that saved and are visible to the client.
export function idsToAnnounce(saved: { id: string; isDraft?: boolean }[]): string[] {
  return saved.filter((a) => a.isDraft !== true).map((a) => a.id)
}

export function describeUploadOutcome({
  savedMessage,
  failedNames,
  notice,
}: {
  savedMessage: string
  failedNames: string[]
  // null when no email was requested
  notice: UploadNoticeStatus | null
}): { severity: UploadNoticeSeverity; message: string } {
  if (failedNames.length > 0) {
    const failed = `Failed to upload ${failedNames.length} file(s): ${failedNames.join(', ')}`
    if (notice === 'sent') {
      return { severity: 'error', message: `${failed}. The client was emailed about the files that saved.` }
    }
    if (notice === 'no_email') {
      return { severity: 'error', message: `${failed}. The client was not emailed: there is no email address on file.` }
    }
    if (notice === 'failed') {
      return { severity: 'error', message: `${failed}. The client could not be emailed about the files that saved.` }
    }
    return { severity: 'error', message: failed }
  }

  if (notice === 'sent') {
    return { severity: 'success', message: `${savedMessage}. The client was emailed.` }
  }
  if (notice === 'no_email') {
    return {
      severity: 'warning',
      message: `${savedMessage}. The client was not emailed: there is no email address on file.`,
    }
  }
  if (notice === 'failed') {
    return { severity: 'error', message: `${savedMessage}, but the client could not be emailed.` }
  }
  return { severity: 'success', message: savedMessage }
}
